/**
 * Size Guide — custom element with accessible inches/cm toggle.
 *
 * Registers <fit-size-guide>. Each instance auto-discovers its toggle
 * button, inches table, and CM table via data attributes. CM cells are
 * pre-populated with inches values server-side (no-JS fallback) and
 * overwritten with converted centimetre values on connection.
 *
 * Lifecycle: A fresh AbortController is created on every connectedCallback
 * so the element works correctly after disconnect/reconnect cycles in the
 * Shopify theme editor.
 */
(function () {
  'use strict';

  if (customElements.get('fit-size-guide')) return;

  class FitSizeGuide extends HTMLElement {
    #toggle;
    #inchesTbl;
    #cmTbl;
    #ctrl;

    connectedCallback() {
      this.#ctrl = new AbortController();
      this.#toggle = this.querySelector('[data-fit-sg-toggle]');
      this.#inchesTbl = this.querySelector('[data-unit="inches"]');
      this.#cmTbl = this.querySelector('[data-unit="cm"]');

      if (!this.#toggle || !this.#inchesTbl || !this.#cmTbl) return;

      this.#populateCmCells();
      this.#toggle.addEventListener('click', this.#onToggle, {
        signal: this.#ctrl.signal,
      });
      this.#applyState(this.#toggle.getAttribute('aria-checked') === 'true');
    }

    disconnectedCallback() {
      this.#ctrl?.abort();
      this.#ctrl = null;
    }

    /* ---- Toggle handler ---- */

    #onToggle = () => {
      const isCm = this.#toggle.getAttribute('aria-checked') !== 'true';
      this.#toggle.setAttribute('aria-checked', String(isCm));
      this.#applyState(isCm);
    };

    #applyState(showCm) {
      this.#inchesTbl.classList.toggle('fit-sg__unit--active', !showCm);
      this.#cmTbl.classList.toggle('fit-sg__unit--active', showCm);
    }

    /* ---- CM conversion ---- */

    #populateCmCells() {
      const cells = this.#cmTbl.querySelectorAll('[data-fit-sg-cell]');
      for (const cell of cells) {
        const raw = (cell.dataset.inches || '').trim();
        if (raw) cell.textContent = FitSizeGuide.#inchesToCm(raw);
      }
    }

    /**
     * Convert an inches text value to centimetres.
     * Handles ranges ("32-34"), single decimals ("26.5"), and integers.
     * Non-numeric text passes through unchanged.
     */
    static #inchesToCm(text) {
      if (text.includes('-')) {
        return text
          .split('-')
          .map((p) => {
            const n = parseFloat(p.trim());
            return isNaN(n) ? p.trim() : Math.round(n * 2.54);
          })
          .join('-');
      }
      const n = parseFloat(text);
      return isNaN(n) ? text : String(Math.round(n * 2.54));
    }
  }

  customElements.define('fit-size-guide', FitSizeGuide);
})();
